import { useNavigate } from 'react-router-dom';
import usePermission from '../../hooks/usePermission';
import React, { useEffect } from 'react';
import {
  HeaderSection,
  AppTable,
  useFetchList,
  PAGE_SIZE_OPTIONS,
  IAppTableColumn,
  BaseResponsePaginationProps,
  removeToken,
} from '@qlibs/react-components';
import { Actions, IAction } from '@qlibs/react-components/dist/Table';
import ListFilterRow from '../../components/ListFilterRow';
import { readListFilter, readListPage } from '../../helpers/listFilterQuery';
import { Alert, Button, Grid, Image, Modal, Table, Tag, message } from 'antd';
import {
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { RiEqualizer2Line } from '@remixicon/react';
import { ColumnsType } from 'antd/lib/table';
import { CompanyProperties } from './types/company.type';
import { httpRequest } from '../../helpers/api';
import { EDateFormat } from '../../const/config';
import { GroupProperties } from '../group/types/group.type';
import LinkTable from '../../components/LinkTable';
import useMasterData from '../../zustand/useGroup';
import {
  formatCurrentDate,
  EFormatCurrentDate,
} from '../../helpers/formatCurrentDate';
import { isRamus2CompanyId } from '../../helpers/ramus2';
import useGroup from '../../zustand/useGroup';
import { useAuthUser, useSignOut } from 'react-auth-kit';
import { ULTRA_SUPER_ADMIN_ROLE_ID } from '../../const/config';
import {
  CompanyMobileFilterActions,
  CompanyMobileFilterApplyButton,
  CompanyMobileFilterButton,
  CompanyMobileFilterDrawer,
  CompanyMobileFilterField,
  CompanyMobileFilterFields,
  CompanyMobileFilterForm,
  CompanyMobileFilterRow,
  CompanyMobileFilterSelect,
  CompanyMobileIconButton,
  CompanyMobilePage,
  CompanyMobilePageButton,
  CompanyMobilePagination,
  CompanyMobilePaginationActions,
  CompanyMobilePaginationInfo,
  CompanyMobileSearch,
  CompanyMobileTableCard,
  MobileCompanyHeader,
} from './mobile';
const moment = require('moment');

const FEATURE = 'COMPANY';

const Company = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { isUserHasPermission } = usePermission();

  const handleCreateCompany = () => {
    navigate({ pathname: '/company/add' });
  };

  const [isModalDeleteOpen, setIsModalDeleteOpen] = React.useState(false);
  const [isModalRestoreOpen, setIsModalRestoreOpen] = React.useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = React.useState(false);
  const [mobileFilterValues, setMobileFilterValues] = React.useState({
    group: 'all',
    status: 'all',
    filterData: 'default',
  });
  const [currentData, setCurrentData] = React.useState<CompanyProperties>();
  const [dataGroup, setDataGroup] = React.useState<GroupProperties[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = React.useState(true);
  const [forceDeleteId, setForceDeleteId] = React.useState<string>();

  const { group } = useMasterData();
  const { company } = useGroup();
  const auth = useAuthUser();
  const isUltraSuperAdmin = auth()?.role?.roleId === ULTRA_SUPER_ADMIN_ROLE_ID;

  const {
    isLoading,
    data,
    pagination,
    query,
    setData,
    setQuery,
    changePage,
    changeLimit,
    handleSearch,
    pageQueries,
    filterDropdown,
  } = useFetchList<CompanyProperties>({
    httpRequest: httpRequest as any,
    endpoint: 'company',
    limit: +PAGE_SIZE_OPTIONS[1],
    initialQuery: {
      filterGroupIds: readListFilter(
        'filterGroupIds',
        isUltraSuperAdmin ? '' : group?.groupId || '',
      ),
      filterStatus: readListFilter('filterStatus'),
      filterArchived: readListFilter('filterArchived'),
      search: readListFilter('search'),
    },
    pageQuery: {
      defaultValue: {
        page: readListPage(),
        filterGroupIds: '',
        filterStatus: '',
        filterArchived: '',
        search: '',
      },
    },
  });

  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));
  const signOut = useSignOut();

  async function deleteData() {
    try {
      if (currentData) {
        const forceDelete =
          forceDeleteId && forceDeleteId === currentData.companyId;
        await httpRequest.delete('/company/' + currentData.companyId, {
          params: {
            forceDelete: forceDelete || undefined,
          },
        });
        message.success(
          `Success ${forceDelete ? 'delete' : 'archive'} ${currentData.name}`,
        );

        setQuery({
          limit: +PAGE_SIZE_OPTIONS[1],
          filterGroupIds: isUltraSuperAdmin ? '' : group?.groupId || '',
        });

        if (currentData.companyId === company.companyId) {
          await delay(1000);
          // relogin
          await httpRequest.post('/auth/signout');
          signOut();
          removeToken();
          localStorage.removeItem('group-selected');
          localStorage.removeItem('map-settings');
          navigate('/login');
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      } else {
        message.error('Current data not found');
      }
    } catch (error: any) {
      message.error(error.data.message);
    } finally {
      setIsModalDeleteOpen(false);
      setCurrentData(undefined);
    }
  }

  async function restoreData() {
    try {
      if (currentData) {
        await httpRequest.patch(
          '/company/' + currentData.companyId + '/restore',
        );
        message.success(`Success restore ${currentData.name}`);

        setQuery({
          limit: +PAGE_SIZE_OPTIONS[1],
        });
      } else {
        message.error('Current data not found');
      }
    } catch (error: any) {
      message.error(error.data.message);
    } finally {
      setIsModalRestoreOpen(false);
      setCurrentData(undefined);
      setForceDeleteId(undefined);
    }
  }

  async function handleRestoreCompany(id: any) {
    setIsModalRestoreOpen(true);
    const found = data.find((item) => item.companyId === id);
    if (found) {
      setCurrentData(found);
    }
  }

  async function handleDeleteCompany(id: any, forced?: boolean) {
    setIsModalDeleteOpen(true);
    const found = data.find((item) => item.companyId === id);
    if (found) {
      setCurrentData(found);
    }

    if (forced) {
      setForceDeleteId(id);
    }
  }

  function handleCancelDelete() {
    setIsModalDeleteOpen(false);
    setIsModalRestoreOpen(false);
    setCurrentData(undefined);
  }

  function isArchived(id?: string) {
    if (id) {
      const found = data.find(
        (item) => item.companyId === id && item.deletedAt,
      );
      if (found) {
        return true;
      }
    }

    return false;
  }

  useEffect(() => {
    const fetchGroup = async () => {
      try {
        setIsLoadingGroups(true);
        const res =
          await httpRequest.get<BaseResponsePaginationProps<GroupProperties>>(
            '/group',
          );
        if (res.data.payload && res.data.payload.results.length) {
          setDataGroup(res.data.payload.results);
        }
      } catch (error: any) {
        message.error(error.data.message);
      } finally {
        setIsLoadingGroups(false);
      }
    };

    fetchGroup();
  }, []);

  const filterGroupOptions = React.useMemo(() => {
    const data = dataGroup.map((item) => ({
      value: item.groupId,
      label: item.name,
    }));
    return [{ value: 'all', label: 'All' }, ...data];
  }, [dataGroup]);

  const selectedGroupFilter =
    pageQueries.filterGroupIds ||
    (isUltraSuperAdmin ? 'all' : group?.groupId || 'all');
  const visibleGroupFilter =
    !isLoadingGroups &&
    filterGroupOptions.some((option) => option.value === selectedGroupFilter)
      ? selectedGroupFilter
      : undefined;

  const getCompanyActions = (record: CompanyProperties): IAction[] => {
    const isReadOnlyRamus2 =
      record.isRamus2 || isRamus2CompanyId(record.companyId);

    return [
      isUserHasPermission([FEATURE + '.DETAIL'])
        ? {
            key: 'detail',
            label: 'Detail',
            onPress: (id?: string) => navigate('/company/' + id),
          }
        : undefined,
      !isReadOnlyRamus2 && isUserHasPermission([FEATURE + '.UPDATE'])
        ? {
            key: 'update',
            label: 'Update',
            onPress: (id?: string) => navigate('/company/' + id + '/edit'),
          }
        : undefined,
      !isReadOnlyRamus2 && isUserHasPermission([FEATURE + '.DELETE'])
        ? {
            key: 'archive',
            label: 'Delete',
            color: '#ff4d4f',
            disabled: (id?: string) => isArchived(id),
            disabledTooltip: 'This data is already archived',
            onPress: (id?: string) => handleDeleteCompany(id),
          }
        : undefined,
      !isReadOnlyRamus2 && isUserHasPermission([FEATURE + '.RESTORE'])
        ? {
            key: 'restore',
            label: 'Restore',
            color: '#1890ff',
            disabled: (id?: string) => !isArchived(id),
            disabledTooltip: 'This data not in archived',
            onPress: (id?: string) => handleRestoreCompany(id),
          }
        : undefined,
      !isReadOnlyRamus2 && isUserHasPermission([FEATURE + '.FORCE_DELETE'])
        ? {
            key: 'delete',
            label: 'Force Delete',
            onPress: (id?: string) => handleDeleteCompany(id, true),
          }
        : undefined,
    ].filter(Boolean) as IAction[];
  };

  const columns: IAppTableColumn<CompanyProperties>[] = [
    {
      title: 'FLAG',
      dataIndex: 'imageLink',
      key: 'imageLink',
      render: (value, _) => (
        <Image
          src={value}
          width={40}
          height={40}
          style={{
            objectFit: 'contain',
            background: '#f4f6f9',
            borderRadius: 4,
          }}
          fallback="/images/flag-not-found.png"
        />
      ),
    },
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      type: 'detail',
      keyId: 'companyId',
      render: (value, records) => (
        <LinkTable onPress={() => navigate('/company/' + records.companyId)}>
          {records.deletedAt ? (
            <Tag color="#ff5500" className="mb-2">
              Archived
            </Tag>
          ) : (
            false
          )}
          {value}
        </LinkTable>
      ),
    },
    {
      title: 'GROUP',
      dataIndex: 'groupId',
      key: 'groupId',
      render: (_, records) =>
        records.group
          ? records.group.nickname
            ? `(${records.group.nickname}) ${records.group.name}`
            : records.group.name || 'N/A'
          : 'N/A',
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (value, _) => (
        <Tag color={value === 'active' ? 'success' : 'error'}>
          {value.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'ASSETS',
      dataIndex: 'assets',
      key: 'assets',
      render: (assets, _) => (assets ? assets.length : 0),
    },
    {
      title: 'CREATED AT',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value, _) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'UPDATED AT',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value, _) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'ACTION',
      key: 'action',
      render: (_, record) => {
        const actions = getCompanyActions(record);

        if (!actions.length) {
          return null;
        }

        return <Actions id={record.companyId} actions={actions} />;
      },
    },
  ];

  const mobileColumns: ColumnsType<CompanyProperties> = [
    {
      title: 'Flag',
      dataIndex: 'imageLink',
      key: 'imageLink',
      width: 80,
      render: (value) => (
        <Image
          src={value}
          width={40}
          height={40}
          style={{
            objectFit: 'contain',
            background: '#f4f6f9',
            borderRadius: 4,
            border: '1px solid #c5cddb',
          }}
          fallback="/images/flag-not-found.png"
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 199,
      render: (value, records) => (
        <LinkTable onPress={() => navigate('/company/' + records.companyId)}>
          {records.deletedAt ? (
            <Tag color="#ff5500" className="mb-2">
              Archived
            </Tag>
          ) : (
            false
          )}
          {value}
        </LinkTable>
      ),
    },
    {
      title: 'Group',
      dataIndex: 'groupId',
      key: 'groupId',
      width: 238,
      render: (_, records) =>
        records.group
          ? records.group.nickname
            ? `(${records.group.nickname}) ${records.group.name}`
            : records.group.name || 'N/A'
          : 'N/A',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 97,
      render: (value) => (
        <Tag color={value === 'active' ? 'success' : 'error'}>
          {String(value || '').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Assets',
      dataIndex: 'assets',
      key: 'assets',
      width: 97,
      render: (assets) => (assets ? assets.length : 0),
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 182,
      render: (value) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'Update at',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 182,
      render: (value) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'Action',
      key: 'action',
      width: 75,
      render: (_, record) => {
        const actions = getCompanyActions(record);

        if (!actions.length) {
          return null;
        }

        return <Actions id={record.companyId} actions={actions} />;
      },
    },
  ];

  const filterData: any = [
    {
      type: 'search',
      key: 'search',
      label: 'Search',
      placeholder: 'Search by company name',
      onChange: (value: any) => {
        handleSearch(value);
      },
      colSpan: isUserHasPermission([FEATURE + '.DELETE']) ? 6 : 10,
    },
    {
      type: 'select',
      key: 'group',
      label: 'Filter Group',
      options: filterGroupOptions,
      onChange: (value: any) => {
        filterDropdown({
          filterGroupIds: value === 'all' ? '' : value,
        });
      },
      colSpan: 8,
    },
    {
      type: 'select',
      key: 'status',
      label: 'Status',
      options: [
        {
          value: 'all',
          label: 'All',
        },
        {
          value: 'active',
          label: 'Active',
        },
        {
          value: 'inactive',
          label: 'Inactive',
        },
      ],
      onChange: (value: any) => {
        filterDropdown({
          filterStatus: value === 'all' ? '' : value,
        });
      },
      colSpan: 6,
    },
    {
      type: 'select',
      key: 'filterData',
      label: 'Filter Data',
      options: [
        {
          value: 'default',
          label: 'Default',
        },
        {
          value: 'show',
          label: 'Include Archive Data',
        },
      ],
      onChange: (value: any) => {
        filterDropdown({
          filterArchived: value === 'default' ? '' : value,
        });
      },
      colSpan: 4,
    },
  ].filter((item) =>
    !isUserHasPermission([FEATURE + '.DELETE'])
      ? item.key !== 'filterData'
      : item,
  );

  const mobilePagination = {
    current: Number((pagination as any)?.page || pageQueries.page || 1),
    pageSize: Number(
      (pagination as any)?.limit || query.limit || +PAGE_SIZE_OPTIONS[1],
    ),
    total: Number((pagination as any)?.total || data.length),
    showSizeChanger: false,
    showTotal: (total: number, range: [number, number]) =>
      `Showing ${range[0]} to ${range[1]} of ${total} results`,
    onChange: (page: number, pageSize: number) => changePage(page, pageSize),
  };
  const mobileCurrentPage = mobilePagination.current;
  const mobilePageSize = mobilePagination.pageSize;
  const mobileTotal = mobilePagination.total;
  const mobileTotalPages = Math.max(1, Math.ceil(mobileTotal / mobilePageSize));
  const mobileStartResult =
    mobileTotal === 0 ? 0 : (mobileCurrentPage - 1) * mobilePageSize + 1;
  const mobileEndResult = Math.min(
    mobileCurrentPage * mobilePageSize,
    mobileTotal,
  );

  const handleMobilePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > mobileTotalPages) {
      return;
    }

    changePage(nextPage, mobilePageSize);
  };

  React.useEffect(() => {
    if (!isMobileFilterOpen) {
      return;
    }

    setMobileFilterValues({
      group:
        (pageQueries as any).group ||
        (pageQueries as any).filterGroupIds ||
        selectedGroupFilter ||
        'all',
      status:
        (pageQueries as any).status ||
        (pageQueries as any).filterStatus ||
        'all',
      filterData:
        (pageQueries as any).filterData ||
        (pageQueries as any).filterArchived ||
        'default',
    });
  }, [isMobileFilterOpen, pageQueries, selectedGroupFilter]);

  const handleApplyMobileFilter = () => {
    filterDropdown({
      filterGroupIds:
        mobileFilterValues.group === 'all' ? '' : mobileFilterValues.group,
      filterStatus:
        mobileFilterValues.status === 'all' ? '' : mobileFilterValues.status,
      filterArchived:
        mobileFilterValues.filterData === 'default'
          ? ''
          : mobileFilterValues.filterData,
    });
    setIsMobileFilterOpen(false);
  };

  return (
    <React.Fragment>
      {isMobile ? (
        <CompanyMobilePage>
          <MobileCompanyHeader
            title="Company"
            onBack={() => navigate(-1)}
            action={
              isUserHasPermission([FEATURE + '.CREATE']) ? (
                <CompanyMobileIconButton
                  type="button"
                  aria-label="Add company"
                  onClick={handleCreateCompany}
                >
                  <PlusOutlined />
                </CompanyMobileIconButton>
              ) : null
            }
          />

          <CompanyMobileFilterRow>
            <CompanyMobileSearch
              allowClear
              defaultValue={pageQueries.search}
              placeholder="Search by company name"
              suffix={<SearchOutlined style={{ color: '#9facbf' }} />}
              onChange={(event) => handleSearch(event.target.value)}
            />
            <CompanyMobileFilterButton
              type="button"
              aria-label="Open company filters"
              onClick={() => setIsMobileFilterOpen(true)}
            >
              <RiEqualizer2Line size={20} />
            </CompanyMobileFilterButton>
          </CompanyMobileFilterRow>

          <CompanyMobileTableCard>
            <Table<CompanyProperties>
              loading={isLoading}
              rowKey="companyId"
              columns={mobileColumns}
              dataSource={data}
              size="small"
              scroll={{ x: 1150 }}
              pagination={false}
            />
          </CompanyMobileTableCard>

          <CompanyMobilePagination>
            <CompanyMobilePaginationInfo>
              Showing {mobileStartResult} to {mobileEndResult} of {mobileTotal}{' '}
              results
            </CompanyMobilePaginationInfo>
            <CompanyMobilePaginationActions>
              <CompanyMobilePageButton
                type="button"
                disabled={mobileCurrentPage <= 1}
                onClick={() => handleMobilePageChange(mobileCurrentPage - 1)}
                aria-label="Previous page"
              >
                <LeftOutlined />
              </CompanyMobilePageButton>
              <CompanyMobilePageButton type="button" $active>
                {mobileCurrentPage}
              </CompanyMobilePageButton>
              {mobileCurrentPage < mobileTotalPages ? (
                <CompanyMobilePageButton
                  type="button"
                  onClick={() => handleMobilePageChange(mobileCurrentPage + 1)}
                >
                  {mobileCurrentPage + 1}
                </CompanyMobilePageButton>
              ) : null}
              <CompanyMobilePageButton
                type="button"
                disabled={mobileCurrentPage >= mobileTotalPages}
                onClick={() => handleMobilePageChange(mobileCurrentPage + 1)}
                aria-label="Next page"
              >
                <RightOutlined />
              </CompanyMobilePageButton>
            </CompanyMobilePaginationActions>
          </CompanyMobilePagination>
        </CompanyMobilePage>
      ) : (
        <>
          <HeaderSection
            title={'Company'}
            rightAction={[
              isUserHasPermission([FEATURE + '.CREATE']) ? (
                <Button type="primary" onClick={handleCreateCompany}>
                  Add Company
                </Button>
              ) : (
                false
              ),
            ]}
          />

          <ListFilterRow
            key={`company-filter-${isLoadingGroups ? 'loading' : 'ready'}-${
              visibleGroupFilter || 'empty'
            }`}
            filterValues={{
              search: pageQueries.search,
              status: pageQueries.filterStatus || 'all',
              filterData: pageQueries.filterArchived || 'default',
              group: visibleGroupFilter,
            }}
            filters={[filterData]}
          />

          <AppTable
            isLoading={isLoading}
            keyId="companyId"
            columns={columns}
            data={data}
            pagination={pagination}
            onChangePage={changePage}
            onChangeLimit={changeLimit}
            // _table={{
            //   scroll:{ x: '100%' }
            // } as any}
          />
        </>
      )}

      <Modal
        title={
          forceDeleteId ? 'Force Delete Confirmation' : 'Delete Confirmation'
        }
        open={isModalDeleteOpen}
        onOk={deleteData}
        okButtonProps={{
          style: {
            background: 'red',
          },
        }}
        onCancel={handleCancelDelete}
      >
        {currentData && currentData?.companyId === company.companyId ? (
          <Alert
            style={{ marginBottom: 16 }}
            message="Warning"
            description="Your are try to remove active company. After delete process you need to re-login"
            type="warning"
            showIcon
          />
        ) : (
          false
        )}

        {forceDeleteId ? (
          <p>
            Are you sure want to permanently delete this data? This data will be
            deleted and only can't be undo.
          </p>
        ) : (
          <p>
            Are you sure want to delete this data? This data will be archived
            and only User with restore permission can undo this action.
          </p>
        )}
      </Modal>

      <Modal
        title="Restore Confirmation"
        open={isModalRestoreOpen}
        onOk={restoreData}
        okButtonProps={{
          style: {
            background: 'blue',
          },
        }}
        onCancel={handleCancelDelete}
      >
        <p>Are you sure want to restore this data?.</p>
      </Modal>

      <CompanyMobileFilterDrawer
        title="Filter"
        placement="bottom"
        height="auto"
        open={isMobileFilterOpen}
        onClose={() => setIsMobileFilterOpen(false)}
      >
        <CompanyMobileFilterForm>
          <CompanyMobileFilterFields>
            <CompanyMobileFilterField>
              Filter Group
              <CompanyMobileFilterSelect
                value={mobileFilterValues.group}
                loading={isLoadingGroups}
                options={filterGroupOptions}
                onChange={(value) =>
                  setMobileFilterValues((prev) => ({
                    ...prev,
                    group: String(value),
                  }))
                }
              />
            </CompanyMobileFilterField>
            <CompanyMobileFilterField>
              Status
              <CompanyMobileFilterSelect
                value={mobileFilterValues.status}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                ]}
                onChange={(value) =>
                  setMobileFilterValues((prev) => ({
                    ...prev,
                    status: String(value),
                  }))
                }
              />
            </CompanyMobileFilterField>
            {isUserHasPermission([FEATURE + '.DELETE']) ? (
              <CompanyMobileFilterField>
                Filter Data
                <CompanyMobileFilterSelect
                  value={mobileFilterValues.filterData}
                  options={[
                    { value: 'default', label: 'All' },
                    { value: 'show', label: 'Include Archive Data' },
                  ]}
                  onChange={(value) =>
                    setMobileFilterValues((prev) => ({
                      ...prev,
                      filterData: String(value),
                    }))
                  }
                />
              </CompanyMobileFilterField>
            ) : null}
          </CompanyMobileFilterFields>
          <CompanyMobileFilterActions>
            <CompanyMobileFilterApplyButton
              type="primary"
              onClick={handleApplyMobileFilter}
            >
              Apply
            </CompanyMobileFilterApplyButton>
          </CompanyMobileFilterActions>
        </CompanyMobileFilterForm>
      </CompanyMobileFilterDrawer>
    </React.Fragment>
  );
};
export default Company;
